import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Icon } from '@iconify/react';
import $ from 'jquery';
import {
  getPaginatedMaintenancePlans,
  createMaintenancePlan,
  updateMaintenancePlan,
  deleteMaintenancePlan
} from '../api/maintenance-planAPI';
import { useFormBuilder } from '../utils/useFormBuilder';
import { useTableFilters } from '../utils/useTableFilters';
import { validateForm } from '../utils/useValidator';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { useSetting } from '../utils/AppSettingsContext';
import { fetchData } from '../utils/apiHelpers';
import { getParts } from '../api/partsAPI';
import { getTrades } from '../api/tradesAPI';
import { getExpenseCodes } from '../api/expense-codeAPI';
import { getExternalServices } from '../api/external-servicesAPI';
import {
  getBillOfMaterialsMpsByMaintenancePlanId,
  createBillOfMaterialsMp,
  updateBillOfMaterialsMp,
  deleteBillOfMaterialsMp
} from '../api/bill-of-materials-mpAPI';
import {
  getBillOfEquipmentMpsByMaintenancePlanId,
  createBillOfEquipmentMp,
  updateBillOfEquipmentMp,
  deleteBillOfEquipmentMp
} from '../api/bill-of-equipment-mpAPI';
import {
  getBillOfLabourMpsByMaintenancePlanId,
  createBillOfLabourMp,
  updateBillOfLabourMp,
  deleteBillOfLabourMp
} from '../api/bill-of-labour-mpAPI';
import {
  getBillOfServicesMpsByMaintenancePlanId,
  createBillOfServicesMp,
  updateBillOfServicesMp,
  deleteBillOfServicesMp
} from '../api/bill-of-services-mpAPI';
import AsyncSelect from 'react-select/async';

const MaintenancePlanListLayer = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [newPlan, setNewPlan] = useState({});
  const [errors, setErrors] = useState({});
  const [, setFormReadOnly] = useState(false);
  const userProfile = useUserProfile();
  const defaultExpenseParts = useSetting('default_expense_parts');
  const defaultExpenseLabour = useSetting('default_expense_labour');
  const defaultExpenseMisc = useSetting('default_expense_misc');

  const [planMaterials, setPlanMaterials] = useState([]);
  const [deletedMaterialIds, setDeletedMaterialIds] = useState([]);
  const [planEquipment, setPlanEquipment] = useState([]);
  const [deletedEquipmentIds, setDeletedEquipmentIds] = useState([]);
  const [planLabour, setPlanLabour] = useState([]);
  const [deletedLabourIds, setDeletedLabourIds] = useState([]);
  const [planServices, setPlanServices] = useState([]);
  const [deletedServiceIds, setDeletedServiceIds] = useState([]);
  const [showPartModal, setShowPartModal] = useState(false);
  const [editingPartIndex, setEditingPartIndex] = useState(null);
  const [partForm, setPartForm] = useState({});
  const [initialPartOptions, setInitialPartOptions] = useState([]);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [editingEquipmentIndex, setEditingEquipmentIndex] = useState(null);
  const [equipmentForm, setEquipmentForm] = useState({});
  const [showLabourModal, setShowLabourModal] = useState(false);
  const [editingLabourIndex, setEditingLabourIndex] = useState(null);
  const [labourForm, setLabourForm] = useState({});
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingServiceIndex, setEditingServiceIndex] = useState(null);
  const [serviceForm, setServiceForm] = useState({});
  const [tradeOptions, setTradeOptions] = useState([]);
  const [expenseOptions, setExpenseOptions] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);

  const listContainerRef = useRef(null);
  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const [plans, setPlans] = useState([]);
  const [planPage, setPlanPage] = useState(1);
  const [planHasMore, setPlanHasMore] = useState(true);
  const [planLoading, setPlanLoading] = useState(false);

  // Specify the input fields for the add / edit form
  // Uses the useFromBuilder hook to generate the form fields for the edit modal
  const inputFields = useMemo(() => [
    { field: 'name', label: 'Name', type: 'text', size: '12', required: true },
    { field: 'code', label: 'Code', type: 'text', size: '6', required: true },
    { field: 'modifierCode', label: 'Modifier Code', type: 'text', size: '4', required: false },
    { field: 'statutory', label: 'Compliance Task', type: 'switch', size: '2', required: false },
    { field: 'groupIds', label: 'Linked Group(s)', type: 'multi-select', size: '6', api: 'groups', displayField: 'groupName', required: false },
    { field: 'taskTypeId', label: 'Task Type', type: 'select', size: '6', api: 'task-types', displayField: 'taskTypeName', required: true },
    { field: 'isCalendar', label: 'Calendar Scheduling?', type: 'switch', size: '3', required: false },
    { field: 'frequency', label: 'Frequency', type: 'number', size: '3', required: true },
    { field: 'measurementUnitId', label: 'Measurement Unit', type: 'select', size: '6', api: 'measurement-units', displayField: 'unitName', required: true },
    { field: 'planningHorizonId', label: 'Default Planning Lead Time', type: 'select', size: '6', api: 'planning-horizon', required: true },
    { field: 'partsCost', label: 'Parts Cost', type: 'number', size: '6' },
    { field: 'defaultPartsExpenseId', label: 'Default Parts Expense', type: 'select', size: '6', api: 'expense-codes', required: true },
    { field: 'labourHours', label: 'Labour Hours', type: 'number', size: '6' },
    { field: 'defaultLabourExpenseId', label: 'Default Labour Expense', type: 'select', size: '6', api: 'expense-codes', required: true },
    { field: 'miscCost', label: 'Misc Cost', type: 'number', size: '6' },
    { field: 'defaultMiscExpenseId', label: 'Default Misc Expense', type: 'select', size: '6', api: 'expense-codes', required: true },
  ], []);

  // Runs the useFormBuilder hook to generate the form fields for the edit modal
  const { renderField } = useFormBuilder(inputFields, newPlan, setNewPlan, errors);

  // Spefies the table filters for the datatable
  const tableFilters = useMemo(() => [
    { field: "groupIds", type: "dropDown", label: "Group(s)", api: "groups", displayField: "groupName" },
    { field: "taskTypeId", type: "dropDown", label: "Task Type", api: "task-types", displayField: "taskTypeName" },
  ], []);

  // Runs the useTableFilters hook to generate the filter dropdowns for the datatable
  const {
    appliedFilters,
    showDropdown,
    setShowDropdown,
    renderFilterDropdown,
    renderFilterBadges,
    resetFilters,
    applyFilters
  } = useTableFilters(tableFilters);

  const updatePlans = async () => {
    setPlans([]);
    setPlanPage(1);
    setPlanHasMore(true);
    const btn = document.getElementById('apply-filters-btn');
    if (btn) btn.click();
  };

  const handlePartSearch = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return [];
    try {
      const result = await getParts({ search: inputValue, pageSize: 10 });
      const items = result.data || result;
      return items.map(p => ({ value: p.id, label: `${p.partNumber} – ${p.description}`, raw: p }));
    } catch (err) {
      console.error('Error fetching parts:', err);
      return [];
    }
  };

  useEffect(() => {
    fetchData(getTrades, {}, setTradeOptions);
    fetchData(getExpenseCodes, {}, setExpenseOptions);
    fetchData(getExternalServices, {}, setServiceOptions);
    (async () => {
      try {
        const result = await getParts({ pageSize: 10 });
        const items = result.data || result;
        const opts = items.map((p) => ({
          value: p.id,
          label: `${p.partNumber} – ${p.description}`,
          raw: p,
        }));
        setInitialPartOptions(opts);
      } catch (err) {
        console.error('Error fetching initial parts:', err);
      }
    })();
  }, []);

  const toDateInputValue = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  useEffect(() => {
    setPlans([]);
    setPlanPage(1);
    setPlanHasMore(true);
  }, [appliedFilters, searchTerm]);

  useEffect(() => {
    const fetchPlans = async () => {
      setPlanLoading(true);
      try {
        const pageSize = planPage === 1 ? 25 : 5;
        const resp = await getPaginatedMaintenancePlans({
          page: planPage,
          pageSize,
          search: searchTerm,
          ...appliedFilters,
        });
        setPlans(prev => [...prev, ...resp.data]);
        setPlanHasMore(planPage * pageSize < resp.total);
      } catch (err) {
        console.error("Error fetching maintenance plans:", err);
      } finally {
        setPlanLoading(false);
      }
    };
    fetchPlans();
  }, [planPage, appliedFilters, searchTerm]);

  useEffect(() => {
    const container = listContainerRef.current;
    if (!container) return;
    const onScroll = () => {
      if (container.scrollTop + container.clientHeight >= container.scrollHeight - 5 && planHasMore && !planLoading) {
        setPlanPage(p => p + 1);
      }
    };
    container.addEventListener("scroll", onScroll);
    return () => container.removeEventListener("scroll", onScroll);
  }, [planHasMore, planLoading]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (plans.length > 0 && tableRef.current) {
      dtRef.current = $(tableRef.current).DataTable({
        paging: false,
        searching: false,
        info: false,
        ordering: false,
        dom: 't',
        buttons: [
          { extend: 'excel', title: 'Task Templates' },
          { extend: 'csv', title: 'Task Templates' },
        ],
        language: { paginate: { first: '', last: '', next: '', previous: '' } },
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [plans]);


  const handleView = (plan) => {
    setFormReadOnly(true);
    setNewPlan({});
    setEditingPlanId(null);

    setTimeout(() => {
      setNewPlan(plan);
      setEditingPlanId(plan.id);
      (async () => {
        try {
          const mats = await getBillOfMaterialsMpsByMaintenancePlanId(plan.id);
          const equip = await getBillOfEquipmentMpsByMaintenancePlanId(plan.id);
          const lab = await getBillOfLabourMpsByMaintenancePlanId(plan.id);
          const serv = await getBillOfServicesMpsByMaintenancePlanId(plan.id);
          setPlanMaterials(mats);
          setPlanEquipment(equip);
          setPlanLabour(lab);
          setPlanServices(serv);
        } catch (err) {
          console.error('Error loading plan details', err);
          setPlanMaterials([]);
          setPlanEquipment([]);
          setPlanLabour([]);
          setPlanServices([]);
        }
        setShowModal(true);
      })();
    }, 0); // Allows state reset before modal re-renders
  };

  const handleEdit = (plan) => {
    setEditingPlanId(null);
    setNewPlan(plan);
    setEditingPlanId(plan.id);
    setFormReadOnly(false);
    (async () => {
      try {
        const mats = await getBillOfMaterialsMpsByMaintenancePlanId(plan.id);
        const equip = await getBillOfEquipmentMpsByMaintenancePlanId(plan.id);
        const lab = await getBillOfLabourMpsByMaintenancePlanId(plan.id);
        const serv = await getBillOfServicesMpsByMaintenancePlanId(plan.id);
        setPlanMaterials(mats);
        setPlanEquipment(equip);
        setPlanLabour(lab);
        setPlanServices(serv);
      } catch (err) {
        console.error('Error loading plan details', err);
        setPlanMaterials([]);
        setPlanEquipment([]);
        setPlanLabour([]);
        setPlanServices([]);
      }
      setShowModal(true);
    })();
  };

  const clearForm = () => {
    setNewPlan({});
    setEditingPlanId(null);
    setErrors({});
    setPlanMaterials([]);
    setPlanEquipment([]);
    setPlanLabour([]);
    setPlanServices([]);
    setDeletedMaterialIds([]);
    setDeletedEquipmentIds([]);
    setDeletedLabourIds([]);
    setDeletedServiceIds([]);
  };

  const closeModal = () => {
    setShowModal(false);
    // document.body.classList.remove('modal-open');
    // const backdrop = document.querySelector('.modal-backdrop');
    // if (backdrop) backdrop.remove();
    clearForm();
  }

  const handleSave = async () => {
    const errors = validateForm(inputFields, newPlan);
    setErrors(errors);
    if (Object.keys(errors).length > 0) return;
    try {

      // strip out any null or object values from newPlan response
      const cleanPayload = {};
      for (const key in newPlan) {
        if (newPlan[key] !== null && typeof newPlan[key] !== 'object') {
          cleanPayload[key] = newPlan[key];
        }
      }

      if (editingPlanId) {
        const planPayload = {
          ...cleanPayload,
          organisationId: userProfile.org_id,
          updatedAt: new Date().toISOString(),
          updatedBy: userProfile.id,
          groupIds: newPlan.groupIds || [],
        }
        await updateMaintenancePlan(editingPlanId, planPayload);

        await Promise.all(
          planMaterials.map(m =>
            m.id ? updateBillOfMaterialsMp(m.id, m) : createBillOfMaterialsMp({ ...m, maintenancePlanId: editingPlanId })
          )
        );
        await Promise.all(
          planEquipment.map(e =>
            e.id ? updateBillOfEquipmentMp(e.id, e) : createBillOfEquipmentMp({ ...e, maintenancePlanId: editingPlanId })
          )
        );
        await Promise.all(
          planLabour.map(l =>
            l.id ? updateBillOfLabourMp(l.id, l) : createBillOfLabourMp({ ...l, maintenancePlanId: editingPlanId })
          )
        );
        await Promise.all(
          planServices.map(s =>
            s.id ? updateBillOfServicesMp(s.id, s) : createBillOfServicesMp({ ...s, maintenancePlanId: editingPlanId })
          )
        );
      } else {
        const planPayload = {
          ...cleanPayload,
          organisationId: userProfile.org_id,
          craetedAt: new Date().toISOString(),
          createdBy: userProfile.id,
          groupIds: newPlan.groupIds || [],
        }
        const created = await createMaintenancePlan(planPayload);
        const newId = created.id;
        await Promise.all(planMaterials.map(m => createBillOfMaterialsMp({ ...m, maintenancePlanId: newId })));
        await Promise.all(planEquipment.map(e => createBillOfEquipmentMp({ ...e, maintenancePlanId: newId })));
        await Promise.all(planLabour.map(l => createBillOfLabourMp({ ...l, maintenancePlanId: newId })));
        await Promise.all(planServices.map(s => createBillOfServicesMp({ ...s, maintenancePlanId: newId })));
      }

      await Promise.all(deletedMaterialIds.map(id => deleteBillOfMaterialsMp(id)));
      await Promise.all(deletedEquipmentIds.map(id => deleteBillOfEquipmentMp(id)));
      await Promise.all(deletedLabourIds.map(id => deleteBillOfLabourMp(id)));
      await Promise.all(deletedServiceIds.map(id => deleteBillOfServicesMp(id)));
      closeModal();
      setEditingPlanId(null);
      await updatePlans();
      setNewPlan({});
      setPlanMaterials([]);
      setPlanEquipment([]);
      setPlanLabour([]);
      setPlanServices([]);
    } catch (error) {
      console.error('Error saving task template:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete Task Template?')) {
      await deleteMaintenancePlan(id);
      setPlans((prev) => prev.filter((a) => a.id !== id));
    }
  };

  const handleExportExcel = () => {
    if (dtRef.current) {
      dtRef.current.button('.buttons-excel').trigger();
    }
  };

  const handleExportCSV = () => {
    if (dtRef.current) {
      dtRef.current.button('.buttons-csv').trigger();
    }
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center">
        <div className="d-flex flex-wrap align-items-center gap-2 ms-3">
          <div className="dropdown">
            <button
              className="w-32-px h-32-px bg-secondary-focus text-secondary-main rounded-circle d-inline-flex align-items-center justify-content-center me-6"
              type="button"
              onClick={() => setShowDropdown(prev => !prev)}
            >
              <Icon icon="mage:filter-2" className="icon text-xl line-height-1" />
            </button>

            <button
              id="reset-filters-btn"
              className="w-32-px h-32-px bg-secondary-focus text-secondary-main rounded-circle d-inline-flex align-items-center justify-content-center me-6"
              onClick={() => resetFilters()}
            >
              <Icon icon="mage:multiply" className="icon text-xl line-height-1" />
            </button>
            <button
              id="apply-filters-btn"
              className="d-none w-32-px h-32-px bg-secondary-focus text-secondary-main rounded-circle d-inline-flex align-items-center justify-content-center me-6"
              onClick={() => applyFilters()}
            >
              <Icon icon="mage:check" className="icon text-xl line-height-1" />
            </button>
            {/* Badges */}
            {renderFilterBadges()}

            {showDropdown && renderFilterDropdown()}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 ms-auto me-8">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="dropdown me-2">
            <button
              className="btn btn-secondary dropdown-toggle"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              Export Data
            </button>
            <ul className="dropdown-menu p-12 border bg-base shadow">
              <li>
                <button className="dropdown-item" onClick={handleExportExcel}>
                  Excel
                </button>
              </li>
              <li>
                <button className="dropdown-item" onClick={handleExportCSV}>
                  CSV
                </button>
              </li>
            </ul>
          </div>
          {userProfile?.privileges?.includes('maintenance-plans:create') && (
            <button
              className="btn btn-primary w-100 text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
              onClick={() => {
                setNewPlan({
                  defaultPartsExpenseId: defaultExpenseParts,
                  defaultLabourExpenseId: defaultExpenseLabour,
                  defaultMiscExpenseId: defaultExpenseMisc
                });
                setEditingPlanId(null);
                setFormReadOnly(false);
                setPlanMaterials([]);
                setPlanEquipment([]);
                setPlanLabour([]);
                setPlanServices([]);
                setShowModal(true)
              }
              }
            >
              <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" /> Add Task Template</button>
          )}
        </div>
      </div>
      <div className="card-body p-0">
        <div className="paginated-table-container scroll-sm" ref={listContainerRef} style={{ maxHeight: 'calc(100vh - 295px)' }}>
          <table className="table bordered-table mb-3" ref={tableRef} style={{ width: '100%', borderRadius: '0' }}>
            <thead>
              <tr>
                <th style={{ borderRadius: '0' }}></th>
                <th>Groups</th>
                <th>Code</th>
                <th>Name</th>
                <th>Frequency</th>
                <th>Task Type</th>
                <th>Created</th>
                <th style={{ borderRadius: '0' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((row) => {
                const baseUrl = process.env.REACT_APP_SPACES_BASE;
                const placeholder = `${process.env.REACT_APP_SPACES_BASE}/placeholder.png`;
                return (
                  <tr key={row.id}>
                    <td className="align-middle">{row.id}</td>
                    <td className="align-middle">
                      {Array.isArray(row.groupDetails) && row.groupDetails.map((group) => {
                        const imageUrl = group?.groupImage?.trim()
                          ? `${baseUrl}/${group.groupImage}`
                          : `${baseUrl}/placeholder.png`;
                        return (
                          <img
                            key={group.id || group.groupName}
                            src={imageUrl}
                            alt={group.groupName}
                            title={group.groupName}
                            style={{
                              width: '45px',
                              height: '45px',
                              borderRadius: '50%',
                              marginRight: '-12px',
                              objectFit: 'cover',
                              border: '1px solid #ccc'
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = placeholder;
                            }}
                          />
                        );
                      })}
                    </td>
                    <td className="align-middle"><strong>{row.code}</strong></td>
                    <td className="align-middle">{row.name}</td>
                    <td className="align-middle">{row.frequency} {row.measurementUnit?.unitCode}</td>
                    <td className="align-middle">{row.taskType?.taskTypeName}</td>
                    <td className="align-middle">{new Date(row.createdAt).toLocaleDateString()}</td>
                    <td className="align-middle">
                      <button className="view-btn w-32-px h-32-px bg-info-focus text-info-main rounded-circle d-inline-flex align-items-center justify-content-center" onClick={() => handleView(row)}>
                        <Icon icon="ic:baseline-visibility" />
                      </button>
                      <button className="edit-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center mx-6" onClick={() => handleEdit(row)}>
                        <Icon icon="ic:baseline-edit" />
                      </button>
                      <button className="delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center" onClick={() => handleDelete(row.id)}>
                        <Icon icon="ic:baseline-delete-outline" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!planLoading && plans.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center py-2">
                    No Task Templates items found
                  </td>
                </tr>
              )}
              {planLoading && (
                <tr>
                  <td colSpan="9" className="text-center py-2">
                    <span className="spinner-border spinner-border-sm" />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {showModal && (
        <div className="modal modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingPlanId ? 'Edit' : 'New'} Task Template</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    closeModal()
                  }
                  } />
              </div>
              <div className="modal-body">
                <ul className="nav nav-tabs" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button className="nav-link active" id="mp-general-tab" data-bs-toggle="tab" data-bs-target="#mp-general" type="button" role="tab">General</button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button className="nav-link" id="mp-materials-tab" data-bs-toggle="tab" data-bs-target="#mp-materials" type="button" role="tab">Parts</button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button className="nav-link" id="mp-labour-tab" data-bs-toggle="tab" data-bs-target="#mp-labour" type="button" role="tab">Labour</button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button className="nav-link" id="mp-services-tab" data-bs-toggle="tab" data-bs-target="#mp-services" type="button" role="tab">Services</button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button className="nav-link" id="mp-equipment-tab" data-bs-toggle="tab" data-bs-target="#mp-equipment" type="button" role="tab">Tools & Resources</button>
                  </li>
                </ul>
                <div className="tab-content pt-3">
                  <div className="tab-pane fade show active" id="mp-general" role="tabpanel">
                    <div className="row g-3">{inputFields.map(renderField)}</div>
                  </div>
                  <div className="tab-pane fade" id="mp-materials" role="tabpanel">
                    <div className="d-flex justify-content-end">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => {
                          setPartForm({
                            partsId: '',
                            optional: 0,
                            quantity: 1,
                            partsPrice: '',
                            pricingDate: new Date().toISOString(),
                            defaultExpenseCode: defaultExpenseParts,
                          });
                          setEditingPartIndex(null);
                          setShowPartModal(true);
                        }}
                      >
                        + Add Part
                      </button>
                    </div>
                    <div className="table-responsive mt-3">
                      <table className="table bordered-table">
                        <thead>
                          <tr>
                            <th>Part Number</th>
                            <th>Description</th>
                            <th>Price</th>
                            <th>Pricing Date</th>
                            <th>Expense</th>
                            <th>Qty</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {planMaterials.map((p, idx) => (
                            <tr key={idx}>
                              <td>{p.parts.partNumber}</td>
                              <td>{p.parts.description}</td>
                              <td>{p.partsPrice}</td>
                              <td>{p.pricingDate ? new Date(p.pricingDate).toLocaleDateString() : ''}</td>
                              <td>{p.defaultExpenseCode2?.name || expenseOptions.find(e => e.id === p.defaultExpenseCode)?.name || p.defaultExpenseCode}</td>
                              <td>{p.quantity}</td>
                              <td>
                                <>
                                  <button className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center me-2" onClick={() => { setEditingPartIndex(idx); setPartForm(p); setShowPartModal(true); }}><Icon icon="ic:baseline-edit" /></button>
                                  <button className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center" onClick={() => { if (window.confirm('Remove?')) { setPlanMaterials(prev => prev.filter((_, i) => i !== idx)); if (p.id) setDeletedMaterialIds(prev => [...prev, p.id]); } }}><Icon icon="ic:baseline-delete-outline" /></button>
                                </>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="tab-pane fade" id="mp-labour" role="tabpanel">
                    <div className="d-flex justify-content-end">
                      <button className="btn btn-primary btn-sm" onClick={() => { setLabourForm({ tradeId: '', qty: 1, labourHours: '', defaultExpenseCode: '', notes: '', cost: '' }); setEditingLabourIndex(null); setShowLabourModal(true); }}>+ Add Labour</button>
                    </div>
                    <div className="table-responsive mt-3">
                      <table className="table bordered-table">
                        <thead><tr><th>Trade</th><th>Qty</th><th>Hours</th><th>Expense</th><th>Cost</th><th>Actions</th></tr></thead>
                        <tbody>
                          {planLabour.map((lb, idx) => (
                            <tr key={idx}>
                              <td>{tradeOptions.find(t => t.id === lb.tradeId)?.name || lb.tradeId}</td>
                              <td>{lb.qty}</td>
                              <td>{lb.labourHours}</td>
                              <td>{expenseOptions.find(e => e.id === lb.defaultExpenseCode)?.name || lb.defaultExpenseCode}</td>
                              <td>{lb.cost}</td>
                              <td><><button className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center me-2" onClick={() => { setEditingLabourIndex(idx); setLabourForm(lb); setShowLabourModal(true); }}><Icon icon="ic:baseline-edit" /></button><button className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center" onClick={() => { if (window.confirm('Delete?')) { setPlanLabour(prev => prev.filter((_, i) => i !== idx)); if (lb.id) setDeletedLabourIds(prev => [...prev, lb.id]); } }}><Icon icon="ic:baseline-delete-outline" /></button></></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="tab-pane fade" id="mp-services" role="tabpanel">
                    <div className="d-flex justify-content-end">
                      <button className="btn btn-primary btn-sm" onClick={() => { setServiceForm({ serviceId: '', quantity: 1, defaultExpenseCode: '', notes: '', cost: '' }); setEditingServiceIndex(null); setShowServiceModal(true); }}>+ Add Service</button>
                    </div>
                    <div className="table-responsive mt-3">
                      <table className="table bordered-table">
                        <thead><tr><th>Service</th><th>Qty</th><th>Expense</th><th>Cost</th><th>Actions</th></tr></thead>
                        <tbody>
                          {planServices.map((sv, idx) => (
                            <tr key={idx}>
                              <td>{serviceOptions.find(s => s.id === sv.serviceId)?.name || sv.serviceId}</td>
                              <td>{sv.quantity}</td>
                              <td>{expenseOptions.find(e => e.id === sv.defaultExpenseCode)?.name || sv.defaultExpenseCode}</td>
                              <td>{sv.cost}</td>
                              <td><><button className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center me-2" onClick={() => { setEditingServiceIndex(idx); setServiceForm(sv); setShowServiceModal(true); }}><Icon icon="ic:baseline-edit" /></button><button className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center" onClick={() => { if (window.confirm('Delete?')) { setPlanServices(prev => prev.filter((_, i) => i !== idx)); if (sv.id) setDeletedServiceIds(prev => [...prev, sv.id]); } }}><Icon icon="ic:baseline-delete-outline" /></button></></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="tab-pane fade" id="mp-equipment" role="tabpanel">
                    <div className="d-flex justify-content-end">
                      <button className="btn btn-primary btn-sm" onClick={() => { setEquipmentForm({ name: '', quantity: 1, notes: '', cost: '' }); setEditingEquipmentIndex(null); setShowEquipmentModal(true); }}>+ Add Resources</button>
                    </div>
                    <div className="table-responsive mt-3">
                      <table className="table bordered-table">
                        <thead><tr><th>Name</th><th>Qty</th><th>Cost</th><th>Actions</th></tr></thead>
                        <tbody>
                          {planEquipment.map((eq, idx) => (
                            <tr key={idx}>
                              <td>{eq.name}</td>
                              <td>{eq.quantity}</td>
                              <td>{eq.cost}</td>
                              <td><><button className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center me-2" onClick={() => { setEditingEquipmentIndex(idx); setEquipmentForm(eq); setShowEquipmentModal(true); }}><Icon icon="ic:baseline-edit" /></button><button className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center" onClick={() => { if (window.confirm('Delete?')) { setPlanEquipment(prev => prev.filter((_, i) => i !== idx)); if (eq.id) setDeletedEquipmentIds(prev => [...prev, eq.id]); } }}><Icon icon="ic:baseline-delete-outline" /></button></></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    closeModal()
                  }
                  }>Close</button>
                <button className="btn btn-primary" onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPartModal && (
        <div className="modal modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingPartIndex !== null ? 'Edit' : 'Add'} Part</h5>
                <button type="button" className="btn-close" onClick={() => setShowPartModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-2">
                  <label className="form-label">Part</label>
                  <AsyncSelect
                    classNamePrefix="react-select"
                    loadOptions={handlePartSearch}
                    defaultOptions={initialPartOptions}
                    cacheOptions
                    value={partForm.parts ? { value: partForm.partsId, label: partForm.parts?.partNumber, raw: partForm.parts } : null}
                    onChange={sel =>
                      setPartForm({
                        ...partForm,
                        partsId: sel.value,
                        parts: sel.raw,
                        partsPrice: sel.raw?.lastPrice,
                        pricingDate: sel.raw?.pricingDate ?? new Date().toISOString(),
                      })
                    }
                    placeholder="Search parts..."
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">Quantity</label>
                  <input type="number" className="form-control" value={partForm.quantity || ''} onChange={e => setPartForm({ ...partForm, quantity: parseInt(e.target.value, 10) })} />
                </div>
                <div className="mb-2">
                  <label className="form-label">Parts Price</label>
                  <input type="number" step="0.01" className="form-control" value={partForm.partsPrice || ''} onChange={e => setPartForm({ ...partForm, partsPrice: e.target.value })} />
                </div>
                <div className="mb-2">
                  <label className="form-label">Pricing Date</label>
                  <input type="date" className="form-control" value={toDateInputValue(partForm.pricingDate)} onChange={e => setPartForm({ ...partForm, pricingDate: e.target.value })} />
                </div>
                <div className="mb-2">
                  <label className="form-label">Expense Code</label>
                  <select className="form-select" value={partForm.defaultExpenseCode || ''} onChange={e => setPartForm({ ...partForm, defaultExpenseCode: parseInt(e.target.value, 10) })}>
                    <option value="">Select…</option>
                    {expenseOptions.map(ec => (<option key={ec.id} value={ec.id}>{ec.name}</option>))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowPartModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={() => {
                  const formData = {
                    ...partForm,
                    partsPrice:
                      partForm.partsPrice ?? partForm.parts?.lastPrice ?? 0,
                    pricingDate:
                      partForm.pricingDate ?? partForm.parts?.pricingDate ?? null,
                    defaultExpenseCode:
                      partForm.defaultExpenseCode ?? defaultExpenseParts,
                    defaultExpenseCode2:
                      expenseOptions.find(
                        (e) => e.id === (partForm.defaultExpenseCode ?? defaultExpenseParts),
                      ) || null,
                  };

                  const duplicate = planMaterials.some((m, i) =>
                    m.partsId === formData.partsId && i !== editingPartIndex
                  );
                  if (duplicate) {
                    alert('Part already added');
                    return;
                  }

                  if (editingPartIndex !== null) {
                    setPlanMaterials(prev => prev.map((m, i) => i === editingPartIndex ? formData : m));
                  } else {
                    setPlanMaterials(prev => [...prev, formData]);
                  }
                  setShowPartModal(false);
                }}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEquipmentModal && (
        <div className="modal modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingEquipmentIndex !== null ? 'Edit' : 'Add'} Tools & Resources</h5>
                <button type="button" className="btn-close" onClick={() => setShowEquipmentModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-2">
                  <label className="form-label">Name</label>
                  <input className="form-control" value={equipmentForm.name || ''} onChange={e => setEquipmentForm({ ...equipmentForm, name: e.target.value })} />
                </div>
                <div className="mb-2">
                  <label className="form-label">Quantity</label>
                  <input type="number" className="form-control" value={equipmentForm.quantity || ''} onChange={e => setEquipmentForm({ ...equipmentForm, quantity: parseInt(e.target.value, 10) })} />
                </div>
                <div className="mb-2">
                  <label className="form-label">Cost</label>
                  <input type="number" step="0.01" className="form-control" value={equipmentForm.cost || ''} onChange={e => setEquipmentForm({ ...equipmentForm, cost: e.target.value })} />
                </div>
                <div className="mb-2">
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" value={equipmentForm.notes || ''} onChange={e => setEquipmentForm({ ...equipmentForm, notes: e.target.value })}></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowEquipmentModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={() => {
                  if (editingEquipmentIndex !== null) {
                    setPlanEquipment(prev => prev.map((e, i) => i === editingEquipmentIndex ? equipmentForm : e));
                  } else {
                    setPlanEquipment(prev => [...prev, equipmentForm]);
                  }
                  setShowEquipmentModal(false);
                }}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showLabourModal && (
        <div className="modal modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingLabourIndex !== null ? 'Edit' : 'Add'} Labour</h5>
                <button type="button" className="btn-close" onClick={() => setShowLabourModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-2">
                  <label className="form-label">Trade</label>
                  <select className="form-select" value={labourForm.tradeId || ''} onChange={e => setLabourForm({ ...labourForm, tradeId: parseInt(e.target.value, 10) })}>
                    <option value="">Select…</option>
                    {tradeOptions.map(t => (<option key={t.id} value={t.id}>{t.name} {t.code}</option>))}
                  </select>
                </div>
                <div className="mb-2">
                  <label className="form-label">Quantity</label>
                  <input type="number" className="form-control" value={labourForm.qty || ''} onChange={e => setLabourForm({ ...labourForm, qty: parseInt(e.target.value, 10) })} />
                </div>
                <div className="mb-2">
                  <label className="form-label">Hours</label>
                  <input type="number" step="0.1" className="form-control" value={labourForm.labourHours || ''} onChange={e => setLabourForm({ ...labourForm, labourHours: e.target.value })} />
                </div>
                <div className="mb-2">
                  <label className="form-label">Expense Code</label>
                  <select className="form-select" value={labourForm.defaultExpenseCode || ''} onChange={e => setLabourForm({ ...labourForm, defaultExpenseCode: parseInt(e.target.value, 10) })}>
                    <option value="">Select…</option>
                    {expenseOptions.map(ec => (<option key={ec.id} value={ec.id}>{ec.name}</option>))}
                  </select>
                </div>
                <div className="mb-2">
                  <label className="form-label">Cost per Hour</label>
                  <input type="number" step="0.01" className="form-control" value={labourForm.cost || ''} onChange={e => setLabourForm({ ...labourForm, cost: e.target.value })} />
                </div>
                <div className="mb-2">
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" value={labourForm.notes || ''} onChange={e => setLabourForm({ ...labourForm, notes: e.target.value })}></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowLabourModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={() => {
                  if (editingLabourIndex !== null) {
                    setPlanLabour(prev => prev.map((l, i) => i === editingLabourIndex ? labourForm : l));
                  } else {
                    setPlanLabour(prev => [...prev, labourForm]);
                  }
                  setShowLabourModal(false);
                }}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showServiceModal && (
        <div className="modal modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingServiceIndex !== null ? 'Edit' : 'Add'} Service</h5>
                <button type="button" className="btn-close" onClick={() => setShowServiceModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-2">
                  <label className="form-label">Service</label>
                  <select className="form-select" value={serviceForm.serviceId || ''} onChange={e => setServiceForm({ ...serviceForm, serviceId: parseInt(e.target.value, 10) })}>
                    <option value="">Select…</option>
                    {serviceOptions.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
                  </select>
                </div>
                <div className="mb-2">
                  <label className="form-label">Quantity</label>
                  <input type="number" className="form-control" value={serviceForm.quantity || ''} onChange={e => setServiceForm({ ...serviceForm, quantity: parseInt(e.target.value, 10) })} />
                </div>
                <div className="mb-2">
                  <label className="form-label">Expense Code</label>
                  <select className="form-select" value={serviceForm.defaultExpenseCode || ''} onChange={e => setServiceForm({ ...serviceForm, defaultExpenseCode: parseInt(e.target.value, 10) })}>
                    <option value="">Select…</option>
                    {expenseOptions.map(ec => (<option key={ec.id} value={ec.id}>{ec.name}</option>))}
                  </select>
                </div>
                <div className="mb-2">
                  <label className="form-label">Cost</label>
                  <input type="number" step="0.01" className="form-control" value={serviceForm.cost || ''} onChange={e => setServiceForm({ ...serviceForm, cost: e.target.value })} />
                </div>
                <div className="mb-2">
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" value={serviceForm.notes || ''} onChange={e => setServiceForm({ ...serviceForm, notes: e.target.value })}></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowServiceModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={() => {
                  if (editingServiceIndex !== null) {
                    setPlanServices(prev => prev.map((s, i) => i === editingServiceIndex ? serviceForm : s));
                  } else {
                    setPlanServices(prev => [...prev, serviceForm]);
                  }
                  setShowServiceModal(false);
                }}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default MaintenancePlanListLayer;
